import type { FollowerDistributionBucket } from '@/types/progress';
import type { DonutSlice } from '@/components/ui/donut-chart';
import { followerRampStep, NEUTRAL_SLOT } from '@/utils/chart-colors';
import { withPercentages, type RawSlice } from './distribution-percent';

// Follower size is ORDERED, so it gets a one-hue ramp instead of seven
// unrelated hues: the reader sees the order in the color.
//
// The endpoint returns the raw `rank` column (1-11, see RANK_RANGES in
// utils/formatters). Eleven slices cannot be stepped in a single hue —
// the lightness gap between neighbours lands at 0.05, under the 0.06 a
// reader needs — and eleven near-identical wedges is not a chart anyone
// can read. So adjacent ranks are paired into six tiers. The boundaries
// fall on round numbers and every tier is a contiguous range, so nothing
// is distorted; the exact rank is still filterable in /creators.
const TIERS = [
  { maxRank: 2, label: '0-1K' },
  { maxRank: 4, label: '1K-10K' },
  { maxRank: 6, label: '10K-50K' },
  { maxRank: 8, label: '50K-250K' },
  { maxRank: 10, label: '250K-750K' },
  { maxRank: 11, label: '>750K' },
];

function tierIndex(rank: number): number {
  return TIERS.findIndex((tier) => rank <= tier.maxRank);
}

// A rank outside 1-11 has no tier to fall in, so it keeps its own neutral
// bucket rather than being dropped — the denominator has to stay equal to
// the other two donuts.
function tierCounts(buckets: FollowerDistributionBucket[]): {
  counts: number[];
  unranked: number;
} {
  const counts = new Array<number>(TIERS.length).fill(0);
  let unranked = 0;
  for (const bucket of buckets) {
    const i = bucket.rank >= 1 ? tierIndex(bucket.rank) : -1;
    if (i === -1) unranked += bucket.count;
    else counts[i] = (counts[i] ?? 0) + bucket.count;
  }
  return { counts, unranked };
}

export function followerSlices(
  buckets: FollowerDistributionBucket[]
): DonutSlice[] {
  const { counts, unranked } = tierCounts(buckets);
  const raw: RawSlice[] = TIERS.map((tier, i) => ({
    id: tier.label,
    label: tier.label,
    value: counts[i] ?? 0,
    color: followerRampStep(i),
  }));
  if (unranked > 0) {
    raw.push({
      id: 'unranked',
      label: 'Sin rango',
      value: unranked,
      color: NEUTRAL_SLOT,
    });
  }
  return withPercentages(raw);
}
